import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import './CreerEvenement.css';
import { evenementService, sousServiceService } from '../../../api';

const CreerEvenement = () => {
  const [formData, setFormData] = useState({
    titre: '',
    description: '',
    lieu: '',
    date: '',
    prix: '',
    sous_service_id: ''
  });
  const [sousServices, setSousServices] = useState([]);
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingSousServices, setLoadingSousServices] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSousServices = async () => {
      try {
        setLoadingSousServices(true);
        setError('');
        const response = await sousServiceService.getAll();
        
        let servicesData = [];
        if (Array.isArray(response)) {
          servicesData = response;
        } else if (response.data && Array.isArray(response.data)) {
          servicesData = response.data;
        } else if (response.result && Array.isArray(response.result)) {
          servicesData = response.result;
        } else {
          throw new Error('Format de réponse non supporté');
        }

        setSousServices(servicesData);
      } catch (err) {
        console.error('Erreur lors du chargement des sous-services:', err);
        setError(`Erreur lors du chargement des sous-services: ${err.message}`);
        setSousServices([]);
      } finally {
        setLoadingSousServices(false);
      }
    };

    fetchSousServices();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      // Validation des champs obligatoires
      const requiredFields = ['titre', 'description', 'lieu', 'date', 'sous_service_id'];
      const missingFields = requiredFields.filter(field => !formData[field]);
      
      if (missingFields.length > 0) {
        throw new Error('Veuillez remplir tous les champs obligatoires');
      }

      if (!image) {
        throw new Error('Veuillez sélectionner une image');
      }

      const data = new FormData();
      
      // Ajout des champs
      data.append('titre', formData.titre);
      data.append('description', formData.description);
      data.append('lieu', formData.lieu);
      data.append('date', formData.date);
      data.append('sous_service_id', formData.sous_service_id);
      
      if (formData.prix) {
        data.append('prix', formData.prix);
      }
      
      // Ajout de l'image - vérification supplémentaire
      if (image) {
        data.append('image', image);
      }
      
      // ID utilisateur (à adapter selon votre système d'authentification)
      data.append('user_id', 1);

      // Appel au service avec les bons headers
      await evenementService.create(data);
      
      setSuccess('Événement créé avec succès!');
      setTimeout(() => navigate('/admin/evenements'), 1500);
    } catch (err) {
      console.error('Erreur lors de la création:', err);
      let errorMessage = 'Une erreur est survenue lors de la création de l\'événement';
      
      if (err.response) {
        if (err.response.status === 422 && err.response.data.errors) {
          // Concaténer toutes les erreurs de validation
          errorMessage = Object.entries(err.response.data.errors)
            .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
            .join('; ');
        } else if (err.response.data?.message) {
          errorMessage = err.response.data.message;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="creer-evenement-container">
      <Link to="/admin/evenements" className="home-arrow">
        <div className="arrow-icon">←</div>
      </Link>
      <h2>Créer un nouvel événement</h2>
      
      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <form onSubmit={handleSubmit} encType="multipart/form-data">
        <div className="form-group">
          <label>Titre *</label>
          <input
            type="text"
            name="titre"
            value={formData.titre}
            onChange={handleChange}
            required
            disabled={isLoading}
          />
        </div>

        <div className="form-group">
          <label>Description *</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            required
            disabled={isLoading}
          />
        </div>

        <div className="form-group">
          <label>Lieu *</label>
          <input
            type="text"
            name="lieu"
            value={formData.lieu}
            onChange={handleChange}
            required
            disabled={isLoading}
          />
        </div>

        <div className="form-group">
          <label>Date et heure *</label>
          <input
            type="datetime-local"
            name="date"
            value={formData.date}
            onChange={handleChange}
            required
            disabled={isLoading}
          />
        </div>

        <div className="form-group">
          <label>Prix (€)</label>
          <input
            type="number"
            name="prix"
            value={formData.prix}
            onChange={handleChange}
            step="0.01"
            min="0"
            placeholder="0.00"
            disabled={isLoading}
          />
        </div>

        <div className="form-group">
          <label>Sous-service *</label>
          <select
            name="sous_service_id"
            value={formData.sous_service_id}
            onChange={handleChange}
            required
            disabled={isLoading || loadingSousServices}
          >
            <option value="">Sélectionnez un sous-service</option>
            {loadingSousServices ? (
              <option value="" disabled>Chargement en cours...</option>
            ) : sousServices.length > 0 ? (
              sousServices.map(service => (
                <option key={service.id} value={service.id}>
                  {service.nom}
                </option>
              ))
            ) : (
              <option value="" disabled>Aucun sous-service disponible</option>
            )}
          </select>
        </div>

        <div className="form-group">
          <label>Image *</label>
          <input
            type="file"
            name="image"
            onChange={handleImageChange}
            accept="image/*"
            required
            disabled={isLoading}
          />
          {preview && (
            <div className="image-preview">
              <img src={preview} alt="Preview" style={{ maxWidth: '200px', maxHeight: '200px', marginTop: '10px' }} />
            </div>
          )}
        </div>

        <button 
          type="submit" 
          className="submit-btn"
          disabled={isLoading || loadingSousServices || sousServices.length === 0}
        >
          {isLoading ? 'Création en cours...' : 'Créer l\'événement'}
        </button>
      </form>
    </div>
  );
};

export default CreerEvenement;